"use client";

import { ImageOff } from "lucide-react";
import NextImage, { type ImageProps } from "next/image";
import * as React from "react";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  SHARED_BLUR_DATA_URL,
  shouldApplyDefaultBlur,
} from "@/lib/image/placeholders";
import { isResponsiveMediaUrl } from "@/lib/media/image-loader";
import { observeMediaProximity } from "@/lib/media/video-proximity";
import { cn } from "@/lib/utils";

export type AppImageFallbackState = {
  src: ImageProps["src"];
  alt: string;
  fill?: boolean;
  width?: ImageProps["width"];
  height?: ImageProps["height"];
};

export type AppImageProps = ImageProps & {
  disableBlur?: boolean;
  forceBlur?: boolean;
  fallback?:
    | React.ReactNode
    | ((state: AppImageFallbackState) => React.ReactNode);
  fallbackClassName?: string;
  imageContext?: string;
};

const srcToLogValue = (src: ImageProps["src"]) => {
  if (typeof src === "string") {
    return src;
  }

  if (src && typeof src === "object" && "src" in src) {
    return src.src;
  }

  return "static-image";
};

const isEmptyImageSrc = (src: ImageProps["src"]) =>
  typeof src === "string" && src.trim().length === 0;

const isAnimatedSvgDataUrl = (
  value: ImageProps["blurDataURL"],
): value is `data:image/${string}` => {
  if (typeof value !== "string") {
    return false;
  }

  const normalized = value.trim();
  if (!normalized.toLowerCase().startsWith("data:image/svg+xml")) {
    return false;
  }

  try {
    return decodeURIComponent(normalized).includes("<animate");
  } catch {
    return (
      normalized.includes("<animate") ||
      normalized.toLowerCase().includes("%3canimate")
    );
  }
};

function DefaultImageFallback({
  alt,
  className,
  fill,
  width,
  height,
}: {
  alt: string;
  className?: string;
  fill?: boolean;
  width?: ImageProps["width"];
  height?: ImageProps["height"];
}) {
  const style =
    !fill && (width || height)
      ? {
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }
      : undefined;

  return (
    <div
      title={alt || undefined}
      className={cn(
        "flex items-center justify-center overflow-hidden bg-linear-to-br from-surface-alt via-surface to-surface-alt text-text-tertiary",
        fill ? "absolute inset-0 h-full w-full" : "h-full min-h-10 w-full",
        className,
      )}
      style={style}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-surface/80 shadow-sm">
        <ImageOff className="h-4 w-4" aria-hidden />
      </div>
    </div>
  );
}

export const AppImage = React.forwardRef<
  React.ComponentRef<typeof NextImage>,
  AppImageProps
>(function AppImage(
  {
    disableBlur = false,
    forceBlur = false,
    placeholder,
    blurDataURL,
    src,
    width,
    height,
    fill,
    alt,
    fallback,
    fallbackClassName,
    imageContext,
    onError,
    onLoad,
    ...props
  },
  ref,
) {
  const [failedSrc, setFailedSrc] = React.useState<string | null>(() =>
    isEmptyImageSrc(src) ? srcToLogValue(src) : null,
  );
  const [retryKey, setRetryKey] = React.useState(0);
  const retries = React.useRef({ src: srcToLogValue(src), count: 0 });
  const currentSrc = srcToLogValue(src);
  const hasFailed = failedSrc === currentSrc;
  React.useEffect(() => {
    if (retries.current.src !== currentSrc)
      retries.current = { src: currentSrc, count: 0 };
    if (!hasFailed || isEmptyImageSrc(src) || retries.current.count >= 2)
      return;
    let attempted = false;
    const retry = () => {
      if (attempted || navigator.onLine === false || retries.current.count >= 2)
        return;
      attempted = true;
      retries.current.count += 1;
      setRetryKey((key) => key + 1);
      setFailedSrc(null);
    };
    const timer = window.setTimeout(retry, 800 * (retries.current.count + 1));
    window.addEventListener("online", retry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", retry);
    };
  }, [currentSrc, hasFailed, src]);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const attach = React.useCallback(
    (node: HTMLImageElement | null) => {
      imageRef.current = node;
      if (typeof ref === "function") return ref(node);
      if (ref) ref.current = node;
    },
    [ref],
  );

  React.useEffect(() => {
    // Re-observe when a reused image element receives a different asset.
    void currentSrc;
    const image = imageRef.current;
    if (!image || hasFailed || image.complete || image.loading === "eager")
      return;
    // Native lazy loading does not consistently look through clipped carousel
    // tracks. Promote the existing responsive image so the browser requests
    // exactly the same srcset candidate that will be displayed after a swipe.
    const stop = observeMediaProximity(image, (nearby) => {
      if (!nearby) return;
      image.loading = "eager";
      stop();
    });
    return stop;
  }, [currentSrc, hasFailed]);

  React.useEffect(() => {
    if (isEmptyImageSrc(src)) {
      setFailedSrc(srcToLogValue(src));
      return;
    }

    setFailedSrc((previous) => (previous === currentSrc ? previous : null));
  }, [currentSrc, src]);

  if (hasFailed) {
    const fallbackState: AppImageFallbackState = {
      src,
      alt,
      fill,
      width,
      height,
    };

    if (typeof fallback === "function") {
      return <>{fallback(fallbackState)}</>;
    }

    return (
      <>
        {fallback ?? (
          <DefaultImageFallback
            alt={alt}
            fill={fill}
            width={width}
            height={height}
            className={fallbackClassName}
          />
        )}
      </>
    );
  }

  const handleError: NonNullable<ImageProps["onError"]> = (event) => {
    const failed = srcToLogValue(src);
    setFailedSrc(failed);
    try {
      captureClientEvent(ANALYTICS_EVENTS.IMAGE_LOAD_FAILED, {
        src: failed,
        alt,
        context: imageContext,
        requested_src: event.currentTarget.currentSrc,
        online: navigator.onLine,
        retry_count: retries.current.count,
        current_path:
          typeof window !== "undefined" ? window.location.pathname : undefined,
      });
    } catch {
      // Image fallback should never be blocked by analytics availability.
    }
    onError?.(event);
  };

  const hasExplicitPlaceholder =
    placeholder !== undefined || blurDataURL !== undefined;

  if (hasExplicitPlaceholder) {
    const normalizedPlaceholder =
      placeholder === "blur" && isAnimatedSvgDataUrl(blurDataURL)
        ? blurDataURL
        : (placeholder ?? "blur");
    const normalizedBlurDataURL =
      normalizedPlaceholder === blurDataURL ? undefined : blurDataURL;

    return (
      <NextImage
        key={`${currentSrc}:${retryKey}`}
        ref={attach}
        src={src}
        width={width}
        height={height}
        fill={fill}
        alt={alt}
        placeholder={normalizedPlaceholder}
        blurDataURL={normalizedBlurDataURL}
        onError={handleError}
        onLoad={onLoad}
        {...props}
        unoptimized={!isResponsiveMediaUrl(src)}
      />
    );
  }

  const shouldBlur = shouldApplyDefaultBlur({
    src,
    width,
    height,
    disableBlur,
    forceBlur,
  });

  return (
    <NextImage
      key={`${currentSrc}:${retryKey}`}
      ref={attach}
      src={src}
      width={width}
      height={height}
      fill={fill}
      alt={alt}
      placeholder={shouldBlur ? SHARED_BLUR_DATA_URL : "empty"}
      onError={handleError}
      onLoad={onLoad}
      {...props}
      unoptimized={!isResponsiveMediaUrl(src)}
    />
  );
});

AppImage.displayName = "AppImage";
